import { useEffect, useRef, useState } from "react";
import { useBreakpointValue as useChakraBreakpointValue } from "@chakra-ui/react";
/**
 * Hook that exposes the result of Chakra UI `useBreakpointValue` but hydrates the result
 * on the first SSR render.
 *
 * This is done to avoid the "className did not match between server and client" error
 * that happens in `@chakra-ui/react@1.0.1`.
 *
 * Refer to:
 * https://github.com/chakra-ui/chakra-ui/issues/2601
 */ export function useBreakpointValue(values, defaultBreakpoint = 'base') {
    const firstRender = useRef(true);
    const [, setTick] = useState(0);
    /**
	 * If values is type object, it is either a ResponsiveObject or ResponsiveArray
	 * so we can keep it this way
	 *
	 * But if values is something else, we "cast" it into a ResponsiveObject so
	 * that for every breakpoint, "values" will be applied
	 *
	 * If values is { base: 'toto', md: 'tata' }, castedValues stays the same
	 * If values is 'toto', castedValues equals { base: 'toto' }
	 *
	 * --> This allows to very easily create custom "ResponsiveValue" props
	 * Ex: If we want to create a custom yOffset ResponsiveValue prop, we can do it by just declaring:
	 *
	 * - yOffset type is ResponsiveValue<number>
	 * - const yOffset = useBreakpoint(props.yOffset)
	 *
	 * And that's it! This way user can specify yOffset as a number, or a responsive prop
	 */ const castedValues = typeof values === 'object' ? values : {
        base: values
    };
    const result = useChakraBreakpointValue(castedValues, defaultBreakpoint);
    /**
	 * Force a re-render after the first SSR render and ensures the return value
	 * of the hook on next render will be the result of the original hook.
	 */ useEffect(()=>{
        if (firstRender.current) {
            setTick((prev)=>prev + 1);
            firstRender.current = false;
        }
    }, []);
    if (firstRender.current) {
        if (Array.isArray(castedValues)) {
            return castedValues[0];
        }
        return castedValues.base;
    }
    return result;
}

//# sourceMappingURL=useBreakpointValue.js.map